import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  InputNumber,
  notification,
  Radio,
  Select,
  Switch,
  Table,
} from "antd";
import { Link } from "react-router-dom";
import {
  blogApi,
  blogTestApi,
  IBlogItem,
  IBlogSettings,
  IBlogSiteOption,
} from "../api";

export const Articles = () => {
  const [type, setType] = useState<"article" | "review">("article");
  const [items, setItems] = useState<IBlogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [isLoading, setIsLoading] = useState(false);

  const [settings, setSettings] = useState<IBlogSettings | null>(null);
  const [isSettingsSaving, setIsSettingsSaving] = useState(false);

  const [siteOptions, setSiteOptions] = useState<IBlogSiteOption[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [isTestRunning, setIsTestRunning] = useState(false);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onSiteSearch = (q: string) => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(async () => {
      try {
        const response = await blogTestApi.searchSites(q);
        if (response.status === "success") {
          setSiteOptions(response.sites);
        }
      } catch (e) {
        // ignore search errors
      }
    }, 400);
  };

  const runTest = async (runType: "article" | "review") => {
    if (!selectedSiteId) {
      return;
    }
    try {
      setIsTestRunning(true);
      await blogTestApi.testRun(selectedSiteId, runType);
      notification.success({
        message: "Started",
        description: `Generation of ${runType} started — it takes 1-2 minutes, then refresh the table`,
      });
    } catch (e) {
      notification.error({
        message: "Error",
        description: "Error starting generation",
      });
    } finally {
      setIsTestRunning(false);
    }
  };

  const loadItems = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await blogApi.getItems(type, page, pageSize);
      if (response.status === "success") {
        setItems(response.items);
        setTotal(response.total);
      }
    } catch (e) {
      notification.error({
        message: "Error",
        description: "Error loading blog items",
      });
    } finally {
      setIsLoading(false);
    }
  }, [type, page, pageSize]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    blogApi
      .getSettings()
      .then((response) => {
        if (response.status === "success") {
          setSettings(response.settings);
        }
      })
      .catch(() => {
        notification.error({
          message: "Error",
          description: "Error loading blog settings",
        });
      });
  }, []);

  const saveSettings = async () => {
    if (!settings) {
      return;
    }
    try {
      setIsSettingsSaving(true);
      const response = await blogApi.updateSettings({
        isArticlesEnabled: settings.isArticlesEnabled,
        isReviewsEnabled: settings.isReviewsEnabled,
        articleIntervalHours: settings.articleIntervalHours,
        reviewIntervalHours: settings.reviewIntervalHours,
        sitesPerTick: settings.sitesPerTick,
        isAiPageImagesEnabled: settings.isAiPageImagesEnabled,
      });
      if (response.status === "success") {
        setSettings(response.settings);
        notification.success({
          message: "Success",
          description: "Settings saved",
        });
      }
    } catch (e) {
      notification.error({
        message: "Error",
        description: "Error saving settings",
      });
    } finally {
      setIsSettingsSaving(false);
    }
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 170,
      render: (value: string) => new Date(value).toLocaleString(),
    },
    {
      title: "Site",
      dataIndex: "siteId",
      key: "site",
      render: (site: IBlogItem["siteId"]) =>
        site ? (
          <Link to={`/sites/${site._id}`}>
            <div>{site.name}</div>
            <div>{site.domain || site.companyLocalName || ""}</div>
          </Link>
        ) : (
          "deleted site"
        ),
    },
    type === "article"
      ? {
          title: "Title",
          dataIndex: "title",
          key: "title",
          render: (title: string, record: IBlogItem) =>
            record.link ? (
              <a href={record.link} target="_blank" rel="noreferrer">
                {title}
              </a>
            ) : (
              title
            ),
        }
      : {
          title: "Author",
          dataIndex: "author",
          key: "author",
          width: 220,
          render: (author: string, record: IBlogItem) =>
            record.link ? (
              <a href={record.link} target="_blank" rel="noreferrer">
                {author}
              </a>
            ) : (
              author
            ),
        },
    {
      title: "Query (keyword)",
      dataIndex: "query",
      key: "query",
    },
    {
      title: "Category type",
      dataIndex: "type",
      key: "type",
      width: 130,
    },
  ];

  return (
    <div>
      <h2>Blog: articles & reviews</h2>
      <Card
        title="Auto-writing settings (global)"
        style={{ marginBottom: 16, maxWidth: 760 }}
        loading={!settings}
      >
        {settings && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Switch
                checked={settings.isArticlesEnabled}
                onChange={(checked) =>
                  setSettings({ ...settings, isArticlesEnabled: checked })
                }
              />
              <span>
                Write articles — every{" "}
                <InputNumber
                  min={1}
                  value={settings.articleIntervalHours}
                  onChange={(value) =>
                    setSettings({
                      ...settings,
                      articleIntervalHours: value || 72,
                    })
                  }
                />{" "}
                hours per site
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Switch
                checked={settings.isReviewsEnabled}
                onChange={(checked) =>
                  setSettings({ ...settings, isReviewsEnabled: checked })
                }
              />
              <span>
                Write reviews — every{" "}
                <InputNumber
                  min={1}
                  value={settings.reviewIntervalHours}
                  onChange={(value) =>
                    setSettings({
                      ...settings,
                      reviewIntervalHours: value || 156,
                    })
                  }
                />{" "}
                hours per site
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span>
                Sites per tick (every 3 min):{" "}
                <InputNumber
                  min={1}
                  max={20}
                  value={settings.sitesPerTick}
                  onChange={(value) =>
                    setSettings({ ...settings, sitesPerTick: value || 3 })
                  }
                />
              </span>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
                borderTop: "1px solid #f0f0f0",
                paddingTop: 12,
              }}
            >
              <Switch
                checked={settings.isAiPageImagesEnabled}
                onChange={(checked) =>
                  setSettings({ ...settings, isAiPageImagesEnabled: checked })
                }
              />
              <span>
                AI-generated page images — unique per site
                <div style={{ color: "#999", fontSize: 12, maxWidth: 520 }}>
                  When on, homepage &amp; category-page images are generated by
                  AI (gpt-image-1.5) per site instead of reused from the shared
                  pool — harder for search engines to fingerprint. Costs an image
                  API call per new image &amp; slows builds; failures fall back to
                  the pool automatically.
                </div>
              </span>
            </div>
            <div>
              <Button
                type="primary"
                loading={isSettingsSaving}
                onClick={saveSettings}
              >
                Save
              </Button>
            </div>
          </div>
        )}
      </Card>

      <Card
        title="Test run — write for one site now (ignores switches and intervals)"
        style={{ marginBottom: 16, maxWidth: 760 }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Select
            showSearch
            allowClear
            placeholder="Search site by name / domain / company"
            style={{ width: 380 }}
            filterOption={false}
            onSearch={onSiteSearch}
            onFocus={() => onSiteSearch("")}
            onChange={(value) => setSelectedSiteId(value || null)}
            options={siteOptions.map((s) => ({
              value: s._id,
              label: `${s.name} — ${s.domain || s.companyLocalName || ""}`,
            }))}
          />
          <Button
            type="primary"
            disabled={!selectedSiteId}
            loading={isTestRunning}
            onClick={() => runTest("article")}
          >
            Write article
          </Button>
          <Button
            disabled={!selectedSiteId}
            loading={isTestRunning}
            onClick={() => runTest("review")}
          >
            Write review
          </Button>
        </div>
      </Card>

      <Radio.Group
        value={type}
        onChange={(e) => {
          setType(e.target.value);
          setPage(1);
        }}
        style={{ marginBottom: 16 }}
      >
        <Radio.Button value="article">Articles</Radio.Button>
        <Radio.Button value="review">Reviews</Radio.Button>
      </Radio.Group>

      <Table
        rowKey="_id"
        loading={isLoading}
        columns={columns}
        dataSource={items}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (t) => `Total ${t} items`,
          onChange: (newPage, newPageSize) => {
            setPage(newPage);
            setPageSize(newPageSize);
          },
        }}
      />
    </div>
  );
};
